import * as React from "react";
import "./SalesOrderListPage.scss";
import Api from "./Api";
import Employee from "./Employee";
import SelectCtl from "./input/SelectCtl";
import Dialog from "./input/Dialog";
import DateRange from "./DateRange";
import { Deal } from "@testout/testout-commerce/models/hubspot/Deal";
import { Customer } from "@testout/testout-commerce/models/commerce/Customer";
import Segment from "@testout/testout-commerce/models/Segment";
import { Territory } from "@testout/testout-commerce/models/commerce/Territory";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { PagedList } from "@testout/testout-commerce/models/PagedList";
import { ISalesOrder, SalesOrder, SalesOrderStatus } from "@testout/testout-commerce/models/commerce/SalesOrder";
import FilterCtl from "./input/FilterCtl";
import { SelectViewCtl, IEmployeeView2 } from "./SelectViewCtl";

// Get the title of the order list from the currently selected filters
export const getViewName = (typeName: string, dateType: string, name: string, values: string[],
    expandedValues: string[], view: IEmployeeView2, defaultTerritory: string): string => {

    var viewName = "";

    // Get a list of the currently selected territories
    var territoryList: string[] = [];
    if (view?.filters && view?.filters["TerritoryCode"])
        for (var i = 0; i < view.filters["TerritoryCode"].length; i++)
            territoryList.push(view.filters["TerritoryCode"][i]);
    // If the territory code filter is changing, use that filter selection instead
    if (name === 'TerritoryCode')
        territoryList = expandedValues;

    // Determine if the user has selected their territory
    var contains = defaultTerritory.split(',').every(v => territoryList.includes(v)) ?? false;
    if (contains)
        viewName = 'My Territory ';

    viewName += typeName + ' ';

    // Get the currently selected date range
    var dateRange: string;
    dateRange = (view?.filters && view?.filters[dateType]) ? view.filters[dateType][0] : "";
    if (name === dateType)
        dateRange = values[0];
    if ((dateRange ?? "") !== "")
        viewName += DateRange.items.filter(item => item.value === dateRange)[0].name;

    return viewName;
}

interface SalesOrderListPageProps { }

interface SalesOrderListPageState {
    errorMessage: string;
    searchInProgress: boolean;
    inProgress: string;
    orders: SalesOrder[];
    defaultView: IEmployeeView2
    view: IEmployeeView2;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    dialogTitle?: string;
    dialogUrl?: string;
    filterDefaultItems: { [name: string]: ListItem };
    employees: ListItem[];
}

export default class SalesOrderListPage extends React.PureComponent<SalesOrderListPageProps, SalesOrderListPageState> {

    constructor(props: SalesOrderListPageProps) {
        super(props);
        const defaultView = {
            name: "",
            type: "SalesOrder",
            employeeNumber: Employee.current?.employeeNumber ?? "",
        };
        this.state = {
            inProgress: "",
            defaultView: defaultView, view: { ...defaultView },
            errorMessage: "", searchInProgress: false, orders: [], employees: [],
            page: 0, rowsPerPage: 25, totalCount: 0, filterDefaultItems: {}
        };
    }

    _searchRef = React.createRef<HTMLInputElement>();

    _rowsPerPageList: ListItem[] = [
        { name: "25 per page", value: "25" },
        { name: "50 per page", value: "50" },
        { name: "100 per page", value: "100" }
    ];

    async componentDidMount() {
        window.addEventListener("message", this.closeWindow);
        await this.initialize();
        this.onRefreshClick();

        const divSearch = this._searchRef.current;
        if (divSearch)
            divSearch.focus();
    }

    componentWillUnmount() {
        window.removeEventListener("message", this.closeWindow);
    }

    closeWindow = () => {
        this.setState({ dialogTitle: "", dialogUrl: "" });
    }

    private async initialize() {
        try {
            // Save the employee list for lookups
            let employees = await Api.get<ListItem[]>('/api/employees/list-items');
            this.setState({ employees: employees });

            const query = new URLSearchParams(window.location.search);
            let filterDefaultItems: { [name: string]: ListItem } = {};

            // Get the default salesperson
            let employeeNumber = Employee.current?.employeeNumber;
            filterDefaultItems["SalespersonCode"] = {
                name: "Me", value: employeeNumber,
                description: "The current user"
            };

            // Get the default territories
            const territories = await Api.get<Territory[]>(`/api/employees/${employeeNumber}/territories`);
            const territoryCodes: string | null = territories && territories.length ? territories.map(t => t.territoryCode).join(",") : null;
            if (territoryCodes)
                filterDefaultItems["TerritoryCode"] = {
                    name: "My Territory", value: territoryCodes,
                    description: "The current user's territories"
                };

            // Get the default customer
            const customerNumber = query.get("customerNumber");
            const customer: Customer | null = customerNumber ? await Api.get<Customer>(`/api/customers/${customerNumber}`) : null;
            if (customerNumber && customer)
                filterDefaultItems["CustomerNumber"] = {
                    name: customer.name ?? "Associated Customer", value: customerNumber,
                    description: "Selected customer"
                };

            // Get the default deal (if any)
            const dealId = query.get("dealId");
            const deal: Deal | null = dealId ? await Api.get<Deal>(`/api/deals/${dealId}`) : null;
            if (dealId && deal)
                filterDefaultItems["DealId"] = {
                    name: deal.dealname ?? "Associated Deal", value: dealId,
                    description: "Selected deal"
                };

            // Set the default view
            let defaultView: IEmployeeView2;
            if (customerNumber && customer)
                defaultView = {
                    ...this.state.defaultView,
                    name: `Sales Orders for ${customer.name}`,
                    filters: { "CustomerNumber": [customerNumber] }
                };
            else if (dealId && deal)
                defaultView = {
                    ...this.state.defaultView,
                    name: `Sales Orders for ${deal.dealname}`,
                    filters: { "DealId": [dealId] }
                };
            else
                defaultView = {
                    ...this.state.defaultView,
                    name: "My Territory Sales Orders Today",
                    filters: territoryCodes
                        ? { "TerritoryCode": territoryCodes.split(','), "OrderDate": ["TODAY"] }
                        : { "OrderDate": ["TODAY"] }
                };

            this.setState({ filterDefaultItems: filterDefaultItems, defaultView: defaultView, view: { ...defaultView } });
        }
        catch (e: any) {
            this.setState({ errorMessage: e.message });
        }
    }

    onFilterSelect = async (name: string, value?: string): Promise<ListItem[]> => {
        let list: ListItem[];
        switch (name) {
            case "SalespersonCode":
                list = value
                    ? [...this.state.employees.filter(e => e.name.toLowerCase().includes(value.toLowerCase()) ||
                        e.value?.toLowerCase().includes(value.toLowerCase()) || e.description?.toLowerCase().includes(value.toLowerCase()))]
                    : [ ...this.state.employees ];
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "TerritoryCode":
                list = value
                    ? await Api.get<ListItem[]>(`/api/territories/list-items?search=${encodeURIComponent(value)}`)
                    : await Api.get<ListItem[]>(`/api/territories/list-items`);
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "CustomerNumber":
                list = value
                    ? await Api.get<ListItem[]>(`/api/customers/list-items?search=${encodeURIComponent(value)}`)
                    : [];
                if (this.state.filterDefaultItems[name])
                    list.unshift(this.state.filterDefaultItems[name]);
                return list;
            case "DealId":
                return this.state.filterDefaultItems[name] ? [this.state.filterDefaultItems[name]] : [];
            case "OrderDate":
                list = DateRange.items;
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            case "Segment":
                list = Segment.items;
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            case "Status":
                list = (Object.values(SalesOrderStatus) as Array<keyof typeof SalesOrderStatus>)
                    .filter(v => typeof (v) === "string").map((status): ListItem => ({ name: status }));
                return value ? list.filter(item => item.name.toLowerCase().includes(value.toLowerCase())) : list;
            default:
                throw new Error(`Filter ${name} is not available.`);
        }
    }

    onFilterChange = (name: string, values: string[]) => {
        let expandedValues: string[] = [];

        // Expand any compound values (separated with comma)
        for (const value of values)
            if (value.includes(",")) {
                for (const subValue in value.split(","))
                    if (!expandedValues.includes(subValue))
                        expandedValues.push(subValue);
            }
            else if (!expandedValues.includes(value))
                expandedValues.push(value);

        let viewName = getViewName('Sales Orders', 'OrderDate', name, values, expandedValues, this.state.view,
            this.state.filterDefaultItems["TerritoryCode"]?.value ?? "");

        this.setState({
            view: {
                ...this.state.view,
                name: viewName,
                filters: { ...this.state.view.filters, [name]: expandedValues }
            },
            defaultView: {
                ...this.state.defaultView,
                name: viewName 
            }
        }, this.onRefreshClick);
    };

    onFilterClear = () => this.setState({ view: { ...this.state.view, filters: {} } }, this.onRefreshClick);

    onSearchChange = (value: string) => this.setState({ view: { ...this.state.view, search: value } }, this.onRefreshClick);

    onViewChange = (view: IEmployeeView2) => this.setState({ view: { ...view } }, this.onRefreshClick);

    onPageChange = (value: number) => this.setState({ page: value }, this.onRefreshClick);

    onRowsPerPageChange = (value: string) => this.setState({ page: 0, rowsPerPage: parseInt(value) }, this.onRefreshClick);

    private _debounceTimer: number | undefined = undefined;
    onRefreshClick = async () => {
        // Limit refresh interval to minimum of 300ms so we don't overwhelm the server
        if (this._debounceTimer)
            window.clearTimeout(this._debounceTimer);
        this._debounceTimer = window.setTimeout(async () => {
            this._debounceTimer = undefined;

            const { view, page, rowsPerPage } = this.state;
            if ((view.search ?? "").length === 0 || (view.search ?? "").length >= 3) {

                // Retrieve the list of orders using the search and filter values
                this.setState({ searchInProgress: true, errorMessage: "" });
                try {
                    let list = await Api.get<PagedList<SalesOrder>>(`/api/orders/list?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? {}))}&page=${page}&size=${rowsPerPage}`);
                    this.setState({
                        orders: list?.items?.map((order: SalesOrder) => new SalesOrder(order)) ?? [],
                        totalCount: list?.totalCount ?? 0
                    });
                }
                catch (error: any) {
                    this.setState({ errorMessage: `Error while retrieving the sales order list: ${error.message}` });
                }
                finally {
                    this.setState({ searchInProgress: false });
                }
            }
        }, 300);
    }

    getPossiblePageNumbers = (): number[] => {
        let startPage = this.state.page - 5;
        if (startPage < 0)
            startPage = 0;
        let array = new Array<number>();
        for (let i = 0; i < 11; i++) {
            array.push(startPage + i);
            if ((startPage + i + 1) * this.state.rowsPerPage >= this.state.totalCount)
                break;
        }
        return array;
    }

    onCreateClick = () => {
        this.setState({ dialogTitle: "Create Sales Order", dialogUrl: "/SalesOrder" })
    }

    onExportClick = async () => {
        try {
            const { view, totalCount } = this.state;
            this.setState({ inProgress: `Exporting ${totalCount.toLocaleString()} records...` });
            await Api.get<PagedList<ISalesOrder>>(`/api/orders/list?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? {}))}&page=-1`);
        }
        catch (error: any) {
            this.setState({ errorMessage: `Error while exporting list: ${error.message}` });
        }
        finally {
            this.setState({ inProgress: "" });
        }
    }

    render() {
        const { defaultView, view, page, rowsPerPage, filterDefaultItems, totalCount } = this.state;

        return <div className="orderListPage">
            <div className="title">
                <div>
                    <SelectViewCtl defaultView={defaultView} view={view} onChange={this.onViewChange} />
                    <div className="actions">
                        <button className="primary" title="Create Customer" onClick={this.onCreateClick}>Create Sales Order</button>
                    </div>
                </div>
                <h6>{totalCount.toLocaleString()} records</h6>
            </div>

            <div className="filters">
                <FilterCtl name="SalespersonCode" label="Owner" values={view.filters?.["SalespersonCode"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="TerritoryCode" label="Territory" values={view.filters?.["TerritoryCode"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="Segment" label="Segment" values={view.filters?.["Segment"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="Status" label="Status" values={view.filters?.["Status"]} multiSelect={true} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="OrderDate" label="Order Date" values={view.filters?.["OrderDate"]} multiSelect={false} hideItemValue={true} minCharsForSearch={1}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                <FilterCtl name="CustomerNumber" label="Customer" values={view.filters?.["CustomerNumber"]} multiSelect={true}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />
                {filterDefaultItems["DealId"] && <FilterCtl name="DealId" label="Deal" values={view.filters?.["DealId"]} multiSelect={false}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />}
                {filterDefaultItems["QuoteNumber"] && <FilterCtl name="QuoteNumber" label="Quote" values={view.filters?.["QuoteNumber"]} multiSelect={false}
                    onSelect={this.onFilterSelect} onChange={this.onFilterChange} />}
                {Object.keys(view.filters ?? {}).find((name: string) => !!view.filters?.[name]?.length) &&
                    <button title="Clear All" className="clear-all" onClick={this.onFilterClear}>Clear All</button>}
            </div>

            <div className="searchDiv">
                <div className="search">
                    <input type="text" ref={this._searchRef}
                        placeholder="Search for order number, PO number, invoice number, or customer email"
                        value={view.search}
                        onChange={(e) => this.onSearchChange(e.target.value)} />
                    <button className={`search${this.state.searchInProgress ? " spinner" : ""}`}
                        title="Search/Refresh"
                        onClick={this.onRefreshClick}
                            disabled={this.state.searchInProgress}></button>
                </div>
                <div className="actions">
                    <a className="tertiary button" title="Export" href={`/api/orders/export?search=${encodeURIComponent(view.search ?? "")}&filters=${encodeURIComponent(JSON.stringify(view.filters ?? "{}"))}`}>Export</a>
                </div>
            </div>

            {this.state.errorMessage &&
                <p className="error">{this.state.errorMessage}</p>}
            <div className="scrollableDiv">
                {this.state.searchInProgress &&
                    <div className="saving"><div><div>Searching...</div><div className="spinner"></div></div></div>}
                <table>
                    <thead>
                        <tr className={this.state.orders.length === 0 ? "empty" : undefined}>
                            <th>ORDER NUMBER</th>
                            <th>STATUS</th>
                            <th>ORDER DATE</th>
                            <th>SHIPPING CUSTOMER</th>
                            <th>BILLING CUSTOMER</th>
                            <th>SALESPERSON</th>
                            <th>TOTAL AMOUNT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.orders.length === 0 && <tr className="empty" key="empty"><td colSpan={7}>No sales orders match the current search text and filters.</td></tr>}
                        {this.state.orders.map((order: SalesOrder) => <tr key={order.orderNumber}>
                            <td><button className="link" tabIndex={0} onClick={() => this.setState({
                                dialogTitle: `${order.orderNumber} - View/Edit Sales Order`,
                                dialogUrl: `/SalesOrder/${order.orderNumber}`
                            })}>{order.orderNumber}</button></td>
                            <td>{SalesOrderStatus[order.status]}</td>
                            <td>{order.orderDate.toLocaleDateString()}</td>
                            <td>{order.shippingCustomer.name ?? order.shippingCustomer.address.companyName ?? order.shippingCustomer.address.contactPerson.toString()}</td>
                            <td>{order.billingCustomer.name ?? order.billingCustomer.address.companyName ?? order.billingCustomer.address.contactPerson.toString()}</td>
                            <td>{this.state.employees.find(e => e.value === order.salespersonCode)?.name ?? order.salespersonCode}</td>
                            <td>{(order.totalAmount)?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
            <div className="pagination">
                <button className="pageButton" disabled={page === 0} onClick={() => this.onPageChange(page - 1)}><span style={{ fontSize: "large" }}>&#10094;</span>&nbsp;&nbsp;Prev</button>
                {this.getPossiblePageNumbers().map((i) => 
                    <button key={i} className={`pageButton ${i === page ? "currentPage" : ""}`}
                        onClick={() => this.onPageChange(i)}>{i + 1}</button>
                )}
                <button className="pageButton" disabled={(page + 1) * rowsPerPage >= totalCount}
                    onClick={() => this.onPageChange(page + 1)}>Next&nbsp;&nbsp;<span style={{ fontSize: "large" }}>&#10095;</span></button>
                <SelectCtl className="page-size" value={rowsPerPage.toString()} onChange={this.onRowsPerPageChange} hideItemValue={true} items={this._rowsPerPageList} />
            </div>

            {this.state.inProgress !== "" &&
                <div className="saving"><div><div>{this.state.inProgress}</div><div className="spinner"></div></div></div>}

            <Dialog isOpen={!!this.state.dialogUrl} title={this.state.dialogTitle || ""} onClose={this.closeWindow}>
                <iframe src={this.state.dialogUrl} title="dialog" frameBorder="none" className="order"></iframe>
            </Dialog>

        </div>;
    }
}